import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, NavLink, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import {
  ArrowRight,
  BadgeCheck,
  Copy,
  KeyRound,
  LockKeyhole,
  MessageSquareText,
  PenLine,
  Plus,
  Ticket,
  UserRound,
} from "lucide-react";
import { trackSuccess } from "./utils/siteAnalytics";
import { providerModels } from "./constants/providers";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { DarkModeProvider, useDarkModeContext } from "./context/DarkModeContext";
import Shell from "./components/Shell";
import Stat from "./components/Stat";
import InterviewList from "./components/InterviewList";
import InterviewRoom from "./components/InterviewRoom";
import LicenseKeyModal from "./components/LicenseKeyModal";
import OutputsPage, { OutputPackagePage } from "./components/OutputsPage";
import CinematicLanding from "./components/CinematicLanding";
import { isAllowedResumeCallback } from "./utils/resumeConnect";

const FEATURE_IMAGE = "https://images.unsplash.com/photo-1560320986-8b3131967584?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMGlkZW50aXR5JTIwcmVmbGVjdGlvbnxlbnwwfHx8Ymx1ZXwxNzc3NTY3MDgwfDA&ixlib=rb-4.1.0&q=85";

// Safely extract a string error message from any Axios/FastAPI error response.
// FastAPI validation errors return detail as an array of Pydantic error objects.
function apiErr(err, fallback = "Something went wrong. Please try again.") {
  const det = err?.response?.data?.detail;
  if (!det) return fallback;
  if (typeof det === "string") return det;
  if (Array.isArray(det)) return det.map((e) => e.msg || String(e)).join(". ");
  return fallback;
}

function LandingPage() {
  const auth = useAuth();
  const ctaTarget = auth.token ? "/app" : "/login";
  return <CinematicLanding ctaTarget={ctaTarget} />;
}

function AuthPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    setNotice("");
    if (mode === "register") {
      if (!form.name || form.name.trim().length < 2) { setError("Please enter your full name (at least 2 characters)."); return; }
      if (form.password.length < 8) { setError("Password must be at least 8 characters."); return; }
    }
    setLoading(true);
    try {
      if (mode === "forgot") {
        const { data } = await auth.client.post("/auth/forgot-password", { email: form.email });
        setNotice(data.message);
        return;
      }
      const endpoint = mode === "login" ? "/auth/login" : "/auth/register";
      const body = mode === "login" ? { email: form.email, password: form.password } : form;
      const { data } = await auth.client.post(endpoint, body);
      if (mode === "register") trackSuccess("signup_completed", data.user?.id);
      auth.saveSession(data);
      const next = searchParams.get("next");
      if (next && next.startsWith("/") && !next.startsWith("//")) navigate(next, { replace: true });
    } catch (err) {
      setError(apiErr(err, "Unable to continue. Please check the form."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="auth-shell" data-testid="auth-page">
      <section className="auth-editorial" data-testid="auth-editorial-panel">
        <div className="brand-mark" data-testid="brand-mark">P</div>
        <div className="editorial-copy">
          <p className="kicker" data-testid="auth-kicker">Precision identity system</p>
          <h1 data-testid="auth-heading">Less direction. Sharper signal. One identity.</h1>
          <p data-testid="auth-description">A restrained reflection space for compressing professional complexity into role, focus, edge, and proof.</p>
        </div>
        <img src={FEATURE_IMAGE} alt="Editorial workspace" data-testid="auth-feature-image" />
      </section>
      <section className="auth-form-panel" data-testid="auth-form-panel">
        <div className="auth-card">
          {mode !== "forgot" && (
            <div className="auth-switch" data-testid="auth-mode-switch">
              <button data-testid="auth-login-tab" className={mode === "login" ? "active" : ""} onClick={() => setMode("login")}>Sign in</button>
              <button data-testid="auth-register-tab" className={mode === "register" ? "active" : ""} onClick={() => setMode("register")}>Create account</button>
            </div>
          )}
          <h2 data-testid="auth-form-title">{mode === "forgot" ? "Reset password" : mode === "login" ? "Welcome back" : "Create your studio account"}</h2>
          <form onSubmit={submit} className="stack" data-testid="auth-form">
            <div className={`auth-field-slide${mode === "register" ? " open" : ""}`}>
              <div className="auth-field-inner">
                <label data-testid="name-label">Full name
                  <input data-testid="auth-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Maya Sen" required={mode === "register"} tabIndex={mode === "register" ? 0 : -1} />
                </label>
              </div>
            </div>
            <label data-testid="email-label">Email
              <input data-testid="auth-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@company.com" required />
            </label>
            {mode !== "forgot" && (
              <label data-testid="password-label">Password
                <input data-testid="auth-password-input" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Minimum 8 characters" required />
              </label>
            )}
            {error && <div className="error-note" data-testid="auth-error-message">{error}</div>}
            {notice && <div className="success-note" data-testid="auth-success-message">{notice}</div>}
            <button className="primary-action" type="submit" disabled={loading} data-testid="auth-submit-button">
              {loading ? "Working..." : mode === "forgot" ? "Send reset instructions" : mode === "login" ? "Enter workspace" : "Create workspace"}
              <ArrowRight size={18} />
            </button>
            {mode === "login" && <button className="link-action" type="button" onClick={() => setMode("forgot")} data-testid="forgot-password-button">Forgot password?</button>}
            {mode === "forgot" && <button className="link-action" type="button" onClick={() => setMode("login")} data-testid="back-to-login-button">Back to sign in</button>}
          </form>
        </div>
      </section>
    </main>
  );
}

function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const auth = useAuth();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    if (!token) { setError("This reset link is missing its token. Please request a new one."); return; }
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (password !== confirmPassword) { setError("Passwords do not match."); return; }
    setLoading(true);
    try {
      await auth.client.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
    } catch (err) {
      setError(apiErr(err, "Unable to reset password. The link may have expired."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="auth-shell" data-testid="reset-password-page">
      <section className="auth-editorial" data-testid="auth-editorial-panel">
        <div className="brand-mark" data-testid="brand-mark">P</div>
        <div className="editorial-copy">
          <p className="kicker" data-testid="reset-password-kicker">Precision identity system</p>
          <h1 data-testid="reset-password-heading">Set a new password.</h1>
          <p data-testid="reset-password-description">Choose a new password to get back into your studio account.</p>
        </div>
        <img src={FEATURE_IMAGE} alt="Editorial workspace" data-testid="reset-password-feature-image" />
      </section>
      <section className="auth-form-panel" data-testid="auth-form-panel">
        <div className="auth-card">
          <h2 data-testid="reset-password-form-title">Reset password</h2>
          {done ? (
            <div className="stack" data-testid="reset-password-success">
              <div className="success-note" data-testid="reset-password-success-message">Your password has been reset.</div>
              <NavLink to="/login" className="primary-action" data-testid="reset-password-login-link">Back to sign in <ArrowRight size={18} /></NavLink>
            </div>
          ) : (
            <form onSubmit={submit} className="stack" data-testid="reset-password-form">
              <label data-testid="reset-new-password-label">New password
                <input data-testid="reset-password-new-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Minimum 8 characters" required />
              </label>
              <label data-testid="reset-confirm-password-label">Confirm password
                <input data-testid="reset-password-confirm-input" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Repeat new password" required />
              </label>
              {!token && <div className="error-note" data-testid="reset-password-missing-token">This link is missing a reset token. Please request a new one from the sign-in page.</div>}
              {error && <div className="error-note" data-testid="reset-password-error-message">{error}</div>}
              <button className="primary-action" type="submit" disabled={loading} data-testid="reset-password-submit-button">
                {loading ? "Working..." : "Reset password"}
                <ArrowRight size={18} />
              </button>
              <NavLink to="/login" className="link-action" data-testid="reset-password-back-link">Back to sign in</NavLink>
            </form>
          )}
        </div>
      </section>
    </main>
  );
}

function Dashboard() {
  const auth = useAuth();
  const [data, setData] = useState(null);
  const [showLicenseModal, setShowLicenseModal] = useState(false);
  const navigate = useNavigate();
  useEffect(() => { auth.client.get("/dashboard").then(({ data }) => setData(data)); }, [auth.client]);
  return (
    <section className="page-grid" data-testid="dashboard-page">
      <div className="page-heading">
        <p className="kicker" data-testid="dashboard-kicker">Executive studio</p>
        <h1 data-testid="dashboard-heading">Shape interviews into career collateral.</h1>
        <button className="primary-action compact" onClick={() => setShowLicenseModal(true)} data-testid="start-interview-button">
          <Plus size={18} /> New interview
        </button>
      </div>
      <div className="stats-row" data-testid="dashboard-stats-row">
        <Stat testid="total-interviews-stat" label="Total interviews" value={data?.total_interviews ?? "—"} icon={MessageSquareText} />
        <Stat testid="active-interviews-stat" label="Active" value={data?.active_interviews ?? "—"} icon={PenLine} />
        <Stat testid="completed-interviews-stat" label="Completed" value={data?.completed_interviews ?? "—"} icon={BadgeCheck} />
      </div>
        <div className="section-band" data-testid="latest-interviews-section">
        <div className="section-title"><h2 data-testid="latest-interviews-heading">Latest interviews</h2><span data-testid="latest-interviews-count">{data?.latest_interviews?.length || 0} recent</span></div>
        {data === null ? <div className="loading-state" data-testid="dashboard-loading">Loading...</div> : <InterviewList interviews={data.latest_interviews || []} />}
      </div>
      {showLicenseModal && (
        <LicenseKeyModal
          onClose={() => setShowLicenseModal(false)}
          onSuccess={(interview) => navigate(`/interview/${interview.id}`)}
        />
      )}
    </section>
  );
}

function InterviewsPage() {
  const auth = useAuth();
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLicenseModal, setShowLicenseModal] = useState(false);
  const navigate = useNavigate();
  useEffect(() => { auth.client.get("/interviews").then(({ data }) => setInterviews(data)).finally(() => setLoading(false)); }, [auth.client]);
  return (
    <section className="page-grid" data-testid="interviews-page">
      <div className="page-heading">
        <p className="kicker" data-testid="interviews-kicker">Interview room</p>
        <h1 data-testid="interviews-heading">Candidate conversations</h1>
        <button className="primary-action compact" onClick={() => setShowLicenseModal(true)} data-testid="interviews-start-button">
          <Plus size={18} /> Start interview
        </button>
      </div>
      {loading ? <div className="loading-state" data-testid="interviews-loading">Loading interviews...</div> : <InterviewList interviews={interviews} />}
      {showLicenseModal && (
        <LicenseKeyModal
          onClose={() => setShowLicenseModal(false)}
          onSuccess={(interview) => navigate(`/interview/${interview.id}`)}
        />
      )}
    </section>
  );
}


function AccountPage() {
  const auth = useAuth();
  const [form, setForm] = useState({ current_password: "", new_password: "" });
  const [saved, setSaved] = useState("");
  const [error, setError] = useState("");
  const submit = async (event) => {
    event.preventDefault();
    setSaved("");
    setError("");
    try {
      const { data } = await auth.client.post("/auth/change-password", form);
      setSaved(data.message);
      setForm({ current_password: "", new_password: "" });
    } catch (err) {
      setError(apiErr(err, "Unable to change password."));
    }
  };
  return (
    <section className="page-grid" data-testid="account-page">
      <div className="page-heading"><p className="kicker" data-testid="account-kicker">Account security</p><h1 data-testid="account-heading">Change your password.</h1></div>
      <form className="account-panel" onSubmit={submit} data-testid="change-password-form">
        <label data-testid="current-password-label">Current password
          <input data-testid="current-password-input" type="password" value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} required />
        </label>
        <label data-testid="new-password-label">New password
          <input data-testid="new-password-input" type="password" value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} minLength="8" required />
        </label>
        {saved && <div className="success-note" data-testid="change-password-success-message">{saved}</div>}
        {error && <div className="error-note" data-testid="change-password-error-message">{error}</div>}
        <button className="primary-action compact" type="submit" data-testid="change-password-submit-button"><LockKeyhole size={18} /> Change password</button>
      </form>
    </section>
  );
}

function AdminPage() {
  const auth = useAuth();
  const [settings, setSettings] = useState({ system_prompt: "", provider: "openai", model: "gpt-5.2", api_key: "", clear_api_key: false });
  const [users, setUsers] = useState([]);
  const [passwords, setPasswords] = useState({});
  const [overview, setOverview] = useState(null);
  const [interviews, setInterviews] = useState([]);
  const [expandedInterview, setExpandedInterview] = useState(null);
  const [saved, setSaved] = useState("");
  const [error, setError] = useState("");
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  // Portfolio CMS state
  const [cmsToggling, setCmsToggling] = useState({});
  const [cmsGrantModal, setCmsGrantModal] = useState(null); // { tempPassword, cmsUrl, userName }
  // License key state
  const KEY_PAGE_SIZE = 50;
  const [licenseKeys, setLicenseKeys] = useState([]);
  const [licenseKeysLoading, setLicenseKeysLoading] = useState(true);
  const [keyStatusFilter, setKeyStatusFilter] = useState("");
  const [keyTierFilter, setKeyTierFilter] = useState("");
  const [keySearchInput, setKeySearchInput] = useState("");
  const [keySearchQuery, setKeySearchQuery] = useState("");
  const [keyPage, setKeyPage] = useState(1);
  const [keyTotal, setKeyTotal] = useState(0);
  const [keyCounts, setKeyCounts] = useState({ unused: 0, redeemed: 0, revoked: 0, total: 0 });
  const [keyGenForm, setKeyGenForm] = useState({ count: 10, tier: "standard", product: "portfolio", notes: "" });
  const [keyGenerating, setKeyGenerating] = useState(false);
  const [freshKeys, setFreshKeys] = useState(null); // array of newly generated { id, key, tier }
  const [keySaved, setKeySaved] = useState("");
  const [keyError, setKeyError] = useState("");
  const [keyRevoking, setKeyRevoking] = useState({});
  const [keyReactivating, setKeyReactivating] = useState({});
  const [keyExporting, setKeyExporting] = useState(false);
  // Interview prompt editor state
  const PROMPT_TYPES = [
    { id: "professional", label: "Professional" },
    { id: "student", label: "Student" },
    { id: "pivot", label: "Pivot" },
  ];
  const [activePromptType, setActivePromptType] = useState("professional");
  const [promptContent, setPromptContent] = useState("");
  const [promptLoading, setPromptLoading] = useState(false);
  const [promptSaved, setPromptSaved] = useState("");
  const [promptError, setPromptError] = useState("");

  useEffect(() => {
    auth.client.get("/admin/settings").then(({ data }) => setSettings({ ...data, api_key: "", clear_api_key: false }));
    auth.client.get("/admin/overview").then(({ data }) => setOverview(data));
    auth.client.get("/admin/users").then(({ data }) => setUsers(data));
    auth.client.get("/admin/interviews").then(({ data }) => setInterviews(data));
  }, [auth.client]);

  // Debounce free-text search so we don't hit the API on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      setKeySearchQuery(keySearchInput.trim());
      setKeyPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [keySearchInput]);

  const refreshLicenseKeys = () => {
    setLicenseKeysLoading(true);
    const params = { page: keyPage, page_size: KEY_PAGE_SIZE };
    if (keyStatusFilter) params.status = keyStatusFilter;
    if (keyTierFilter) params.tier = keyTierFilter;
    if (keySearchQuery) params.q = keySearchQuery;
    return auth.client.get("/admin/license-keys", { params })
      .then(({ data }) => {
        setLicenseKeys(data.items);
        setKeyTotal(data.total);
        setKeyCounts(data.counts);
      })
      .finally(() => setLicenseKeysLoading(false));
  };
  useEffect(() => { refreshLicenseKeys(); }, [auth.client, keyStatusFilter, keyTierFilter, keySearchQuery, keyPage]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setPromptLoading(true);
    setPromptContent("");
    setPromptSaved("");
    setPromptError("");
    auth.client.get(`/admin/prompts/${activePromptType}`)
      .then(({ data }) => setPromptContent(data.content || ""))
      .catch(() => setPromptError("Could not load prompt."))
      .finally(() => setPromptLoading(false));
  }, [auth.client, activePromptType]);

  const savePrompt = async () => {
    setPromptSaved(""); setPromptError("");
    try {
      await auth.client.put(`/admin/prompts/${activePromptType}`, { content: promptContent });
      setPromptSaved("Prompt saved.");
    } catch (e) {
      setPromptError(apiErr(e, "Could not save prompt."));
    }
  };
  const refreshUsers = () => auth.client.get("/admin/users").then(({ data }) => setUsers(data));
  const setProvider = (provider) => setSettings((previous) => ({ ...previous, provider, model: providerModels[provider]?.[0] || "" }));
  const testLlm = async () => {
    setTestResult(null);
    setTesting(true);
    try {
      const { data } = await auth.client.post("/admin/test-llm", {
        provider: settings.provider,
        model: settings.model,
        api_key: settings.api_key || undefined,
      });
      setTestResult(data);
    } catch (err) {
      setTestResult({ ok: false, stage: "network", message: apiErr(err, "Request to test endpoint failed.") });
    } finally {
      setTesting(false);
    }
  };
  const save = async (event) => {
    event.preventDefault();
    setSaved("");
    setError("");
    setSavingSettings(true);
    try {
      const { data } = await auth.client.put("/admin/settings", settings);
      setSettings({ ...data, api_key: "", clear_api_key: false });
      setSaved("Settings saved. Connection changes are validated before activation.");
    } catch (err) {
      setError(apiErr(err, "Configuration was not activated."));
    } finally {
      setSavingSettings(false);
    }
  };
  const toggleLock = async (user) => {
    setSaved("");
    setError("");
    try {
      await auth.client.patch(`/admin/users/${user.id}/lock`, { locked: !user.locked });
      setSaved(user.locked ? "User unlocked." : "User locked.");
      refreshUsers();
    } catch (err) {
      setError(apiErr(err, "Unable to update lock status."));
    }
  };
  const resetUserPassword = async (user) => {
    setSaved("");
    setError("");
    try {
      await auth.client.patch(`/admin/users/${user.id}/password`, { new_password: passwords[user.id] || "" });
      setSaved(`Password reset for ${user.email}.`);
      setPasswords((previous) => ({ ...previous, [user.id]: "" }));
    } catch (err) {
      setError(apiErr(err, "Unable to reset user password."));
    }
  };
  const toggleCmsAccess = async (user) => {
    setCmsToggling((prev) => ({ ...prev, [user.id]: true }));
    setSaved(""); setError("");
    try {
      if (user.has_cms_access) {
        await auth.client.delete(`/admin/users/${user.id}/cms-access`);
        setSaved(`CMS access revoked for ${user.name}.`);
        setUsers((prev) => prev.map((u) => u.id === user.id ? { ...u, has_cms_access: false } : u));
      } else {
        const { data } = await auth.client.post(`/admin/users/${user.id}/cms-access`, { tier: "premium" });
        setSaved(`CMS access granted for ${user.name}.`);
        setUsers((prev) => prev.map((u) => u.id === user.id ? { ...u, has_cms_access: true, portfolio_tenant_id: data.tenantId } : u));
        setCmsGrantModal({ tempPassword: data.tempPassword, cmsUrl: data.cmsUrl, userName: user.name });
      }
    } catch (err) {
      setError(apiErr(err, "Failed to update CMS access."));
    } finally {
      setCmsToggling((prev) => ({ ...prev, [user.id]: false }));
    }
  };
  const generateKeys = async (event) => {
    event.preventDefault();
    setKeySaved(""); setKeyError(""); setFreshKeys(null);
    setKeyGenerating(true);
    try {
      const { data } = await auth.client.post("/admin/license-keys", {
        count: Number(keyGenForm.count),
        tier: keyGenForm.tier,
        product: keyGenForm.product,
        notes: keyGenForm.notes,
      });
      setFreshKeys(data.keys);
      setKeySaved(`Generated ${data.keys.length} license key(s).`);
      setKeyPage(1);
      refreshLicenseKeys();
    } catch (err) {
      setKeyError(apiErr(err, "Could not generate license keys."));
    } finally {
      setKeyGenerating(false);
    }
  };
  const copyAllKeys = () => {
    if (!freshKeys?.length) return;
    navigator.clipboard?.writeText(freshKeys.map((k) => k.key).join("\n"));
  };
  const downloadCsvBlob = (csvContent, filename) => {
    // csvContent may be a string (client-built CSV) or a Blob (server export response).
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };
  const downloadFreshKeysCsv = () => {
    if (!freshKeys?.length) return;
    const rows = ["key,tier", ...freshKeys.map((k) => `${k.key},${k.tier}`)];
    downloadCsvBlob(rows.join("\n"), `license-keys-${new Date().toISOString().slice(0, 10)}.csv`);
  };
  const exportLicenseKeysCsv = async () => {
    setKeyExporting(true);
    setKeyError("");
    try {
      const params = {};
      if (keyStatusFilter) params.status = keyStatusFilter;
      if (keyTierFilter) params.tier = keyTierFilter;
      if (keySearchQuery) params.q = keySearchQuery;
      const response = await auth.client.get("/admin/license-keys/export", { params, responseType: "blob" });
      downloadCsvBlob(response.data, `license-keys-${new Date().toISOString().slice(0, 10)}.csv`);
    } catch (err) {
      setKeyError(apiErr(err, "Could not export license keys."));
    } finally {
      setKeyExporting(false);
    }
  };
  const revokeKey = async (key) => {
    if (!window.confirm(`Revoke license key ${key.key}? This cannot be undone.`)) return;
    setKeyRevoking((prev) => ({ ...prev, [key.id]: true }));
    setKeySaved(""); setKeyError("");
    try {
      await auth.client.post(`/admin/license-keys/${key.id}/revoke`);
      setKeySaved("License key revoked.");
      refreshLicenseKeys();
    } catch (err) {
      setKeyError(apiErr(err, "Could not revoke license key."));
    } finally {
      setKeyRevoking((prev) => ({ ...prev, [key.id]: false }));
    }
  };
  const reactivateKey = async (key) => {
    if (!window.confirm(`Reactivate license key ${key.key}? It will become unused again.`)) return;
    setKeyReactivating((prev) => ({ ...prev, [key.id]: true }));
    setKeySaved(""); setKeyError("");
    try {
      await auth.client.post(`/admin/license-keys/${key.id}/reactivate`);
      setKeySaved("License key reactivated.");
      refreshLicenseKeys();
    } catch (err) {
      setKeyError(apiErr(err, "Could not reactivate license key."));
    } finally {
      setKeyReactivating((prev) => ({ ...prev, [key.id]: false }));
    }
  };
  const keyTotalPages = Math.max(1, Math.ceil(keyTotal / KEY_PAGE_SIZE));
  return (
    <section className="admin-layout" data-testid="admin-page">
      <div className="page-heading"><p className="kicker" data-testid="admin-kicker">Control room</p><h1 data-testid="admin-heading">Model, prompt, and key management.</h1></div>
      <div className="stats-row" data-testid="admin-stats-row">
        <Stat testid="admin-users-stat" label="Users" value={overview?.users ?? "—"} icon={UserRound} />
        <Stat testid="admin-interviews-stat" label="Interviews" value={overview?.interviews ?? "—"} icon={MessageSquareText} />
        <Stat testid="admin-completed-stat" label="Completed" value={overview?.completed ?? "—"} icon={BadgeCheck} />
      </div>
      <form className="settings-panel" onSubmit={save} data-testid="admin-settings-form">
        <label data-testid="system-prompt-label">System prompt
          <textarea data-testid="system-prompt-input" value={settings.system_prompt} onChange={(e) => setSettings((previous) => ({ ...previous, system_prompt: e.target.value }))} />
        </label>
        <div className="two-column">
          <label data-testid="provider-label">Provider
            <select data-testid="provider-select" value={settings.provider} onChange={(e) => setProvider(e.target.value)}>
              {Object.keys(providerModels).map((provider) => <option key={provider} value={provider}>{provider}</option>)}
            </select>
          </label>
          <label data-testid="model-label">Model
            <input data-testid="model-input" value={settings.model} onChange={(e) => setSettings((previous) => ({ ...previous, model: e.target.value }))} list="model-options" />
            <datalist id="model-options">{(providerModels[settings.provider] || []).map((model) => <option key={model} value={model} />)}</datalist>
          </label>
        </div>
        <label data-testid="api-key-label">API key <span data-testid="api-key-status">{settings.has_api_key ? `saved ${settings.api_key_mask}` : "not saved"}</span>
          <input data-testid="api-key-input" value={settings.api_key || ""} onChange={(e) => setSettings((previous) => ({ ...previous, api_key: e.target.value }))} type="password" placeholder="Paste a provider API key, or leave blank to keep existing" />
        </label>
        <label className="check-line" data-testid="clear-api-key-label">
          <input data-testid="clear-api-key-checkbox" type="checkbox" checked={!!settings.clear_api_key} onChange={(e) => setSettings((previous) => ({ ...previous, clear_api_key: e.target.checked }))} />
          Clear saved API key
        </label>
        <p className="settings-guidance" data-testid="admin-settings-guidance">
          Provider, model, or key changes are tested before activation. If validation fails, the current working configuration stays active.
        </p>
        {saved && <div className="success-note" data-testid="admin-success-message">{saved}</div>}
        {error && <div className="error-note" data-testid="admin-error-message">{error}</div>}
        {testResult && (
          <div className={testResult.ok ? "success-note" : "error-note"} data-testid="llm-test-result">
            <strong style={{display:"block",marginBottom:".35rem"}}>
              {testResult.ok ? "✓ Connection successful" : `✗ Failed at: ${testResult.stage}`}
            </strong>
            {testResult.message}
            {testResult.preview && <span style={{display:"block",marginTop:".4rem",opacity:.75,fontSize:".85rem"}}>Response preview: {testResult.preview}</span>}
          </div>
        )}
        <div style={{display:"flex",gap:".75rem",flexWrap:"wrap"}}>
          <button className="primary-action compact" type="submit" disabled={savingSettings || testing} data-testid="save-admin-settings-button"><KeyRound size={18} /> {savingSettings ? "Validating..." : "Validate & save"}</button>
          <button className="ghost-action small" type="button" onClick={testLlm} disabled={testing || savingSettings} data-testid="test-llm-button" style={{width:"auto"}}>
            {testing ? "Testing..." : "Test only"}
          </button>
        </div>
      </form>
      <section className="section-band" data-testid="admin-users-section">
        <div className="section-title"><h2 data-testid="admin-users-heading">Users</h2><span data-testid="admin-users-count">{users.length} accounts</span></div>
        <div className="user-table" data-testid="admin-users-list">
          {users.map((user) => (
            <article className="user-row" key={user.id} data-testid={`admin-user-row-${user.id}`}>
              <div className="user-meta" data-testid={`admin-user-meta-${user.id}`}>
                <strong data-testid={`admin-user-name-${user.id}`}>{user.name}</strong>
                <span data-testid={`admin-user-email-${user.id}`}>{user.email}</span>
                <span className={`status-pill ${user.locked ? "complete" : ""}`} data-testid={`admin-user-status-${user.id}`}>{user.locked ? "locked" : user.role}</span>
              </div>
              <div className="inline-actions" data-testid={`admin-user-actions-${user.id}`}>
                <input data-testid={`admin-reset-password-input-${user.id}`} type="password" value={passwords[user.id] || ""} onChange={(e) => setPasswords((previous) => ({ ...previous, [user.id]: e.target.value }))} placeholder="New password" />
                <button className="ghost-action small" type="button" onClick={() => resetUserPassword(user)} data-testid={`admin-reset-password-button-${user.id}`}>Reset</button>
                <button className="ghost-action small" type="button" onClick={() => toggleLock(user)} data-testid={`admin-lock-user-button-${user.id}`}>{user.locked ? "Unlock" : "Lock"}</button>
              </div>
            </article>
          ))}
        </div>
      </section>
      <section className="section-band" data-testid="admin-interviews-section">
        <div className="section-title">
          <h2 data-testid="admin-interviews-heading">All interviews</h2>
          <span data-testid="admin-interviews-count">{interviews.length} total</span>
        </div>
        <div className="user-table" data-testid="admin-interviews-list">
          {interviews.map((iv) => (
            <article className="user-row" key={iv.id} data-testid={`admin-interview-row-${iv.id}`}>
              <div className="user-meta" data-testid={`admin-interview-meta-${iv.id}`}>
                <strong data-testid={`admin-interview-user-${iv.id}`}>{iv.user_name}</strong>
                <span data-testid={`admin-interview-email-${iv.id}`}>{iv.user_email}</span>
                <span data-testid={`admin-interview-title-${iv.id}`} style={{ opacity: 0.6, fontSize: ".8rem" }}>{iv.title}</span>
                <span className={`status-pill ${iv.status}`} data-testid={`admin-interview-status-${iv.id}`}>{iv.status}</span>
              </div>
              <div className="inline-actions" data-testid={`admin-interview-actions-${iv.id}`}>
                <span style={{ fontSize: ".75rem", opacity: 0.5 }}>{iv.created_at?.slice(0, 10)}</span>
                {iv.result_json && (
                  <button
                    className="ghost-action small"
                    type="button"
                    onClick={() => setExpandedInterview(expandedInterview === iv.id ? null : iv.id)}
                    data-testid={`admin-interview-expand-${iv.id}`}
                  >
                    {expandedInterview === iv.id ? "Hide data" : "View data"}
                  </button>
                )}
              </div>
              {expandedInterview === iv.id && iv.result_json && (
                <div className="admin-interview-data" data-testid={`admin-interview-data-${iv.id}`} style={{ gridColumn: "1 / -1", marginTop: ".75rem" }}>
                  {iv.result_json.linkedin_summary && (
                    <>
                      <p className="mono-label" style={{ fontSize: ".68rem", marginBottom: ".5rem" }}>LinkedIn summary</p>
                      <div style={{ marginBottom: "1rem", lineHeight: 1.6, fontSize: ".85rem" }}>
                        {iv.result_json.linkedin_summary.split(/\n\n+/).filter(Boolean).map((para, i) => (
                          <p key={i} style={{ marginBottom: ".6rem", color: "var(--text)" }}>{para}</p>
                        ))}
                      </div>
                    </>
                  )}
                  <p className="mono-label" style={{ fontSize: ".68rem", marginBottom: ".5rem" }}>Full JSON</p>
                  <pre className="json-output" data-testid={`admin-interview-json-${iv.id}`} style={{ fontSize: ".7rem" }}>
                    {JSON.stringify(iv.result_json, null, 2)}
                  </pre>
                </div>
              )}
            </article>
          ))}
          {!interviews.length && <div className="empty-state">No interviews yet.</div>}
        </div>
      </section>

      {/* Interview Prompts */}
      <section className="section-band" data-testid="admin-prompts-section">
        <div className="section-title">
          <h2 data-testid="admin-prompts-heading">Interview Prompts</h2>
          <span>Used for new interviews — changes take effect immediately</span>
        </div>
        <div className="auth-switch" style={{ marginBottom: "1rem", maxWidth: "360px" }}>
          {PROMPT_TYPES.map((pt) => (
            <button
              key={pt.id}
              data-testid={`prompt-tab-${pt.id}`}
              className={activePromptType === pt.id ? "active" : ""}
              onClick={() => setActivePromptType(pt.id)}
            >
              {pt.label}
            </button>
          ))}
        </div>
        {promptLoading ? (
          <div className="loading-state">Loading prompt…</div>
        ) : (
          <div className="stack" style={{ marginTop: 0 }}>
            <label data-testid="prompt-content-label">
              {PROMPT_TYPES.find(p => p.id === activePromptType)?.label} interview prompt
              <textarea
                data-testid="prompt-content-input"
                value={promptContent}
                onChange={(e) => setPromptContent(e.target.value)}
                style={{ minHeight: "320px", fontFamily: "monospace", fontSize: ".82rem" }}
              />
            </label>
            {promptSaved && <div className="success-note" data-testid="prompt-saved-message">{promptSaved}</div>}
            {promptError && <div className="error-note" data-testid="prompt-error-message">{promptError}</div>}
            <button className="primary-action compact" type="button" onClick={savePrompt} data-testid="save-prompt-button">
              Save prompt
            </button>
          </div>
        )}
      </section>

      {/* Portfolio CMS Access */}
      <section className="section-band" data-testid="admin-cms-section">
        <div className="section-title">
          <h2 data-testid="admin-cms-heading">Portfolio CMS Access</h2>
          <span data-testid="admin-cms-count">{users.filter((u) => u.has_cms_access).length} active</span>
        </div>
        <div className="user-table" data-testid="admin-cms-list">
          {users.map((user) => (
            <article className="user-row" key={user.id} data-testid={`admin-cms-row-${user.id}`}>
              <div className="user-meta" data-testid={`admin-cms-meta-${user.id}`}>
                <strong data-testid={`admin-cms-name-${user.id}`}>{user.name}</strong>
                <span data-testid={`admin-cms-email-${user.id}`}>{user.email}</span>
                {user.portfolio_tenant_id && (
                  <span style={{ fontSize: ".75rem", opacity: 0.55 }} data-testid={`admin-cms-tenant-${user.id}`}>
                    tenant: {user.portfolio_tenant_id}
                  </span>
                )}
              </div>
              <div className="inline-actions" data-testid={`admin-cms-actions-${user.id}`}>
                <span className={`status-pill ${user.has_cms_access ? "complete" : ""}`} data-testid={`admin-cms-status-${user.id}`}>
                  {user.has_cms_access ? "CMS enabled" : "no CMS"}
                </span>
                <button
                  className={`ghost-action small ${user.has_cms_access ? "" : "primary-action compact"}`}
                  style={user.has_cms_access ? {} : { padding: ".25rem .75rem" }}
                  type="button"
                  disabled={!!cmsToggling[user.id]}
                  onClick={() => toggleCmsAccess(user)}
                  data-testid={`admin-cms-toggle-${user.id}`}
                >
                  {cmsToggling[user.id] ? "…" : user.has_cms_access ? "Revoke" : "Grant access"}
                </button>
              </div>
            </article>
          ))}
          {!users.length && <div className="empty-state">No users yet.</div>}
        </div>
      </section>

      {/* License Keys */}
      <section className="section-band" data-testid="admin-license-keys-section">
        <div className="section-title">
          <h2 data-testid="admin-license-keys-heading">License Keys</h2>
          <span data-testid="admin-license-keys-count">{keyCounts.total} total</span>
        </div>

        <form className="settings-panel" onSubmit={generateKeys} data-testid="license-key-generate-form" style={{ marginTop: 0 }}>
          <div className="two-column">
            <label data-testid="license-key-count-label">Count
              <input
                type="number"
                min={1}
                max={1000}
                data-testid="license-key-count-input"
                value={keyGenForm.count}
                onChange={(e) => setKeyGenForm((prev) => ({ ...prev, count: e.target.value }))}
              />
            </label>
            <label data-testid="license-key-tier-label">Tier
              <select
                data-testid="license-key-tier-select"
                value={keyGenForm.tier}
                onChange={(e) => setKeyGenForm((prev) => ({ ...prev, tier: e.target.value }))}
              >
                <option value="standard">Standard</option>
                <option value="premium">Premium</option>
              </select>
            </label>
            <label data-testid="license-key-product-label">Product
              <select
                data-testid="license-key-product-select"
                value={keyGenForm.product}
                onChange={(e) => setKeyGenForm((prev) => ({ ...prev, product: e.target.value }))}
              >
                <option value="portfolio">Portfolio interview (PP)</option>
                <option value="resume">Résumé workspace (PR)</option>
              </select>
            </label>
          </div>
          <div className="inline-actions" style={{ flexWrap: "wrap" }} data-testid="license-key-count-chips">
            {[10, 50, 100, 500, 1000].map((n) => (
              <button
                key={n}
                type="button"
                className="ghost-action small"
                data-testid={`license-key-count-chip-${n}`}
                onClick={() => setKeyGenForm((prev) => ({ ...prev, count: n }))}
              >
                {n}
              </button>
            ))}
          </div>
          <label data-testid="license-key-notes-label">Notes <span style={{ opacity: .6, fontWeight: 400 }}>(optional)</span>
            <input
              data-testid="license-key-notes-input"
              value={keyGenForm.notes}
              onChange={(e) => setKeyGenForm((prev) => ({ ...prev, notes: e.target.value }))}
              placeholder="e.g. Batch for LinkedIn campaign"
            />
          </label>
          {keySaved && <div className="success-note" data-testid="license-key-success-message">{keySaved}</div>}
          {keyError && <div className="error-note" data-testid="license-key-error-message">{keyError}</div>}
          <button className="primary-action compact" type="submit" disabled={keyGenerating} data-testid="license-key-generate-button">
            <Ticket size={18} /> {keyGenerating ? "Generating…" : "Generate keys"}
          </button>
        </form>

        {freshKeys && (
          <div className="info-note" data-testid="fresh-license-keys-block" style={{ marginTop: "1rem" }}>
            <p style={{ marginBottom: ".5rem" }}>New keys — copy these now. Unused keys stay visible in full in the table below.</p>
            <pre className="json-output" data-testid="fresh-license-keys-list" style={{ fontSize: ".82rem" }}>
              {freshKeys.map((k) => k.key).join("\n")}
            </pre>
            <div className="inline-actions">
              <button className="ghost-action small" type="button" onClick={copyAllKeys} data-testid="copy-all-license-keys-button">
                <Copy size={16} /> Copy all
              </button>
              <button className="ghost-action small" type="button" onClick={downloadFreshKeysCsv} data-testid="download-fresh-license-keys-csv-button">
                Download CSV
              </button>
            </div>
          </div>
        )}

        <div className="license-keys-toolbar" style={{ marginTop: "1.25rem", display: "flex", flexDirection: "column", gap: ".85rem" }} data-testid="license-keys-toolbar">
          <label data-testid="license-key-search-label" style={{ maxWidth: "420px" }}>Search
            <input
              data-testid="license-key-search"
              value={keySearchInput}
              onChange={(e) => setKeySearchInput(e.target.value)}
              placeholder="Search by key prefix, notes, redeemer email or name"
            />
          </label>
          <div className="auth-switch" data-testid="license-key-status-tabs" style={{ maxWidth: "520px" }}>
            <button type="button" className={keyStatusFilter === "" ? "active" : ""} onClick={() => { setKeyStatusFilter(""); setKeyPage(1); }} data-testid="license-key-status-tab-all">
              All {keyCounts.total}
            </button>
            <button type="button" className={keyStatusFilter === "unused" ? "active" : ""} onClick={() => { setKeyStatusFilter("unused"); setKeyPage(1); }} data-testid="license-key-status-tab-unused">
              Unused {keyCounts.unused}
            </button>
            <button type="button" className={keyStatusFilter === "redeemed" ? "active" : ""} onClick={() => { setKeyStatusFilter("redeemed"); setKeyPage(1); }} data-testid="license-key-status-tab-redeemed">
              Redeemed {keyCounts.redeemed}
            </button>
            <button type="button" className={keyStatusFilter === "revoked" ? "active" : ""} onClick={() => { setKeyStatusFilter("revoked"); setKeyPage(1); }} data-testid="license-key-status-tab-revoked">
              Revoked {keyCounts.revoked}
            </button>
          </div>
          <div className="two-column" style={{ maxWidth: "560px", alignItems: "end" }}>
            <label data-testid="license-key-tier-filter-label">Tier
              <select
                data-testid="license-key-tier-filter-select"
                value={keyTierFilter}
                onChange={(e) => { setKeyTierFilter(e.target.value); setKeyPage(1); }}
              >
                <option value="">All tiers</option>
                <option value="standard">Standard</option>
                <option value="premium">Premium</option>
              </select>
            </label>
            <button className="ghost-action small" type="button" disabled={keyExporting} onClick={exportLicenseKeysCsv} data-testid="license-key-export-button">
              {keyExporting ? "Exporting…" : "Export CSV"}
            </button>
          </div>
        </div>

        <div className="user-table" data-testid="license-keys-list" style={{ marginTop: "1rem" }}>
          {licenseKeysLoading ? (
            <div className="loading-state" data-testid="license-keys-loading">Loading license keys...</div>
          ) : licenseKeys.length ? (
            licenseKeys.map((key) => (
              <article className="user-row" key={key.id} data-testid={`license-key-row-${key.id}`}>
                <div className="user-meta" data-testid={`license-key-meta-${key.id}`}>
                  <strong data-testid={`license-key-value-${key.id}`} style={{ fontFamily: "monospace" }}>{key.key}</strong>
                  <span data-testid={`license-key-tier-${key.id}`}>{key.tier}</span>
                  {key.notes && <span data-testid={`license-key-notes-${key.id}`} style={{ opacity: .6 }}>{key.notes}</span>}
                  {key.redeemer_name && (
                    <span data-testid={`license-key-redeemer-${key.id}`} style={{ opacity: .75 }}>
                      Redeemed by {key.redeemer_name} ({key.redeemer_email}){key.redeemed_at ? ` on ${key.redeemed_at.slice(0, 10)}` : ""}
                    </span>
                  )}
                </div>
                <div className="inline-actions" data-testid={`license-key-actions-${key.id}`}>
                  <span className={`status-pill ${key.status === "redeemed" ? "complete" : ""}`} data-testid={`license-key-status-${key.id}`}>{key.status}</span>
                  {key.status === "unused" && (
                    <button
                      className="ghost-action small"
                      type="button"
                      onClick={() => navigator.clipboard?.writeText(key.key)}
                      data-testid={`license-key-copy-button-${key.id}`}
                    >
                      <Copy size={16} /> Copy
                    </button>
                  )}
                  {key.status === "unused" && (
                    <button
                      className="ghost-action small"
                      type="button"
                      disabled={!!keyRevoking[key.id]}
                      onClick={() => revokeKey(key)}
                      data-testid={`license-key-revoke-button-${key.id}`}
                    >
                      {keyRevoking[key.id] ? "…" : "Revoke"}
                    </button>
                  )}
                  {key.status === "revoked" && (
                    <button
                      className="ghost-action small"
                      type="button"
                      disabled={!!keyReactivating[key.id]}
                      onClick={() => reactivateKey(key)}
                      data-testid={`license-key-reactivate-button-${key.id}`}
                    >
                      {keyReactivating[key.id] ? "…" : "Reactivate"}
                    </button>
                  )}
                </div>
              </article>
            ))
          ) : (
            <div className="empty-state" data-testid="license-keys-empty-state">No license keys yet.</div>
          )}
        </div>

        <div className="inline-actions" style={{ marginTop: "1rem", justifyContent: "space-between" }} data-testid="license-keys-pagination">
          <button
            className="ghost-action small"
            type="button"
            disabled={keyPage <= 1}
            onClick={() => setKeyPage((p) => Math.max(1, p - 1))}
            data-testid="license-keys-prev-page-button"
          >
            Previous
          </button>
          <span data-testid="license-keys-page-indicator">Page {keyPage} of {keyTotalPages}</span>
          <button
            className="ghost-action small"
            type="button"
            disabled={keyPage >= keyTotalPages}
            onClick={() => setKeyPage((p) => Math.min(keyTotalPages, p + 1))}
            data-testid="license-keys-next-page-button"
          >
            Next
          </button>
        </div>
      </section>

      {/* CMS Grant Modal */}
      {cmsGrantModal && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: "1rem" }}
          data-testid="cms-grant-modal"
        >
          <div style={{ background: "var(--bg)", borderRadius: "1rem", padding: "2rem", maxWidth: "440px", width: "100%", boxShadow: "0 25px 50px rgba(0,0,0,.25)" }}>
            <h3 style={{ margin: "0 0 .75rem", fontSize: "1.1rem" }}>CMS access granted ✓</h3>
            <p style={{ margin: "0 0 1.25rem", fontSize: ".875rem", opacity: .7 }}>
              Share these credentials with {cmsGrantModal.userName}. The welcome email has already been sent.
            </p>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: ".875rem", marginBottom: "1.5rem" }}>
              <tbody>
                <tr>
                  <td style={{ padding: ".5rem .75rem", background: "var(--bg)", fontWeight: 600, width: "35%", color: "var(--text)" }}>Login URL</td>
                  <td style={{ padding: ".5rem .75rem", background: "var(--surface)", color: "var(--text)" }}>
                    <a href={cmsGrantModal.cmsUrl} target="_blank" rel="noopener noreferrer">{cmsGrantModal.cmsUrl}</a>
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: ".5rem .75rem", background: "var(--bg)", fontWeight: 600, color: "var(--text)" }}>Temp password</td>
                  <td style={{ padding: ".5rem .75rem", background: "var(--surface)", fontFamily: "monospace", color: "var(--text)" }}>{cmsGrantModal.tempPassword}</td>
                </tr>
              </tbody>
            </table>
            <button
              className="primary-action compact"
              type="button"
              style={{ width: "100%" }}
              onClick={() => setCmsGrantModal(null)}
              data-testid="cms-grant-modal-close"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

function Protected({ children, adminOnly = false }) {
  const auth = useAuth();
  if (!auth.token) return <Navigate to="/login" replace />;
  if (adminOnly && auth.user?.role !== "admin") return <Navigate to="/app" replace />;
  return <Shell>{children}</Shell>;
}

function ResumeConnectPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState("");
  const returnTo = searchParams.get("return_to") || "";

  useEffect(() => {
    if (!isAllowedResumeCallback(returnTo)) { setError("This résumé sign-in destination is not allowed."); return; }
    if (!auth.token) {
      const next = `/resume-connect?return_to=${encodeURIComponent(returnTo)}`;
      navigate(`/login?next=${encodeURIComponent(next)}`, { replace: true });
      return;
    }
    auth.client.post("/auth/resume-connect")
      .then(({ data }) => window.location.assign(`${returnTo}?code=${encodeURIComponent(data.code)}`))
      .catch((reason) => setError(apiErr(reason, "Résumé sign-in could not be completed.")));
  }, [auth.client, auth.token, navigate, returnTo]);

  return <main className="loading-state" style={{ minHeight: "100vh", display: "grid", placeContent: "center" }}>
    {error || "Securely connecting your Prime Portfolio account to Résumé…"}
  </main>;
}

function AnalyticsPrivacyBoundary({ children }) {
  const { pathname } = useLocation();
  return <div data-presagio-block={pathname === "/" ? undefined : ""}>{children}</div>;
}

function LoginRoute() {
  const auth = useAuth();
  return auth.token ? <Navigate to="/app" /> : <AuthPage />;
}

function App() {
  useEffect(() => {
    const theme = (process.env.REACT_APP_TYPOGRAPHY_THEME || "B").toLowerCase();
    document.documentElement.setAttribute("data-theme", theme);
  }, []);

  return (
    <AuthProvider>
      <DarkModeProvider>
        <div className="App" data-testid="career-atelier-app">
          <BrowserRouter>
            <AnalyticsPrivacyBoundary><Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<LoginRoute />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/resume-connect" element={<ResumeConnectPage />} />
              <Route path="/app" element={<Protected><Dashboard /></Protected>} />
              <Route path="/interviews" element={<Protected><InterviewsPage /></Protected>} />
              <Route path="/interview/:id" element={<Protected><InterviewRoom /></Protected>} />
              <Route path="/outputs" element={<Protected><OutputsPage /></Protected>} />
              <Route path="/outputs/:packageId" element={<Protected><OutputPackagePage /></Protected>} />
              <Route path="/results" element={<Navigate to="/outputs" replace />} />
              <Route path="/account" element={<Protected><AccountPage /></Protected>} />
              <Route path="/admin" element={<Protected adminOnly><AdminPage /></Protected>} />
              <Route path="*" element={<Navigate to="/" />} />
            </Routes></AnalyticsPrivacyBoundary>
          </BrowserRouter>
        </div>
      </DarkModeProvider>
    </AuthProvider>
  );
}

export default App;
